import { Badge } from '@shared/components/ui/badge';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownActions } from '../components/interaction/DropdownActions';
import { Client } from '../models/Clients';

// 🎯 Fecha como dd/mm/yyyy (alineado con otros listados)
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

// 🎯 Función utilitaria para renderizar valores de forma segura
const safeRender = (value: any): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return value.toString();

  // Si es un objeto, intentar extraer propiedades comunes
  if (typeof value === 'object') {
    if (value.name) return value.name;
    if (value.description) return value.description;
    if (value.title) return value.title;
    if (value.label) return value.label;
    if (value.id) return value.id.toString();
  }

  return '-';
};

// 🎯 Función para convertir ColDef de AG Grid a ColumnDef de TanStack para ColumnSelector
const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<Client>[] => {
  return agGridColumns
    .filter((col) => {
      // 🎯 Excluir columnas que no deben ser opcionales
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((col) => ({
      id: col.field || col.headerName || 'unknown',
      accessorKey: col.field || col.headerName || 'unknown',
      header: col.headerName || 'Columna',
      cell: col.cellRenderer ? () => null : undefined, // ColumnSelector no necesita cell renderers
    }));
};

// 🎯 Tipos para los handlers
interface UseAgGridColumnsProps {
  selectedColumns?: string[];
  onImpersonate?: (userId: number) => void;
}

// 🎯 Hook para manejar las columnas de AG Grid y ColumnSelector
export const useAgGridColumns = ({ selectedColumns = [], onImpersonate }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('clientsModule');

  // 🎯 Definiciones completas de columnas para AG Grid
  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // 🔵 COLUMNA: Nombre del cliente
      {
        field: 'name',
        headerName: t('table.columns.name'),
        minWidth: 180,
        // 🎯 CELDA: Nombre del cliente
        cellRenderer: (params: any) => {
          const client = params.data as Client;
          return <div className="text-sm font-medium text-title">{safeRender(client.name)}</div>;
        },
      },

      // 🔵 COLUMNA: Nombre del usuario
      {
        field: 'user.name',
        headerName: t('table.columns.userName'),
        minWidth: 150,
        valueGetter: (params) => params.data.user?.name,
        // 🎯 CELDA: Nombre del usuario
        cellRenderer: (params: any) => {
          const client = params.data as Client;
          return <div className="text-sm text-title">{safeRender(client.user?.name)}</div>;
        },
      },

      // 🔵 COLUMNA: Email del usuario
      {
        field: 'user.email',
        headerName: t('table.columns.email'),
        minWidth: 200,
        valueGetter: (params) => params.data.user?.email,
        // 🎯 CELDA: Email del usuario
        cellRenderer: (params: any) => {
          const client = params.data as Client;
          return <div className="text-sm text-complementary-text">{safeRender(client.user?.email)}</div>;
        },
      },

      // 🔵 COLUMNA: Teléfono del usuario
      {
        field: 'user.phone',
        headerName: t('table.columns.phone'),
        width: 130,
        valueGetter: (params) => params.data.user?.phone,
        // 🎯 CELDA: Teléfono del usuario
        cellRenderer: (params: any) => {
          const client = params.data as Client;
          return <div className="text-sm text-title">{safeRender(client.user?.phone)}</div>;
        },
      },

      // 🔵 COLUMNA: País
      {
        field: 'country',
        headerName: t('table.columns.country'),
        width: 100,
        // 🎯 CELDA: Badge con el código del país
        cellRenderer: (params: any) => {
          const client = params.data as Client;
          return (
            <Badge variant="outline" className="font-mono text-xs">
              {safeRender(client.country)}
            </Badge>
          );
        },
      },

      // 🔵 COLUMNA: Ciudad
      {
        field: 'city',
        headerName: t('table.columns.city'),
        minWidth: 120,
        // 🎯 CELDA: Ciudad del cliente
        cellRenderer: (params: any) => {
          const client = params.data as Client;
          return <div className="text-sm text-title">{safeRender(client.city)}</div>;
        },
      },

      // 🔵 COLUMNA: Fecha de creación
      {
        field: 'created_at',
        headerName: t('table.columns.createdAt'),
        width: 130,
        minWidth: 120,
        cellRenderer: (params: any) => {
          const client = params.data as Client;
          return <div className="text-sm text-complementary-text tabular-nums">{formatDate(client.created_at)}</div>;
        },
      },

      // 🔵 COLUMNA: Tipo de Empresa
      {
        field: 'beeping_team.company_type',
        headerName: t('table.columns.companyType'),
        width: 150,
        valueGetter: (params) => params.data.beeping_team?.company_type,
        // 🎯 CELDA: Badge con el tipo de empresa
        cellRenderer: (params: any) => {
          const client = params.data as Client;
          const companyType = client.beeping_team?.company_type;
          if (!companyType) return <span className="text-sm text-muted-foreground">-</span>;

          const variant = companyType === 'sl' ? 'default' : 'secondary';
          return (
            <Badge variant={variant} className="text-xs">
              {t(`table.companyTypes.${companyType}`)}
            </Badge>
          );
        },
      },

      // 🔵 COLUMNA: Origen del Cliente
      {
        field: 'beeping_team.customer_origin',
        headerName: t('table.columns.customerOrigin'),
        width: 140,
        valueGetter: (params) => params.data.beeping_team?.customer_origin,
        // 🎯 CELDA: Badge con el origen del cliente
        cellRenderer: (params: any) => {
          const client = params.data as Client;
          const origin = client.beeping_team?.customer_origin;
          if (!origin) return <span className="text-sm text-muted-foreground">-</span>;

          return (
            <Badge variant="outline" className="text-xs">
              {t(`table.customerOrigins.${origin}`)}
            </Badge>
          );
        },
      },

      // 🔵 COLUMNA: Frecuencia de Facturación
      {
        field: 'beeping_team.billing_frequency',
        headerName: t('table.columns.billingFrequency'),
        width: 140,
        valueGetter: (params) => params.data.beeping_team?.billing_frequency,
        // 🎯 CELDA: Frecuencia de facturación
        cellRenderer: (params: any) => {
          const client = params.data as Client;
          const frequency = client.beeping_team?.billing_frequency;
          if (!frequency) return <span className="text-sm text-muted-foreground">-</span>;

          return <div className="text-sm text-title">{t(`table.billingFrequencies.${frequency}`)}</div>;
        },
      },

      // 🔵 COLUMNA: Tipo de Pedido
      {
        field: 'beeping_team.order_type',
        headerName: t('table.columns.orderType'),
        width: 130,
        valueGetter: (params) => params.data.beeping_team?.order_type,
        // 🎯 CELDA: Badge con el tipo de pedido
        cellRenderer: (params: any) => {
          const client = params.data as Client;
          const orderType = client.beeping_team?.order_type;
          if (!orderType) return <span className="text-sm text-muted-foreground">-</span>;

          const variantMap: Record<string, 'default' | 'secondary' | 'outline'> = {
            cod: 'default',
            card: 'secondary',
            hybrid: 'outline',
          };

          return (
            <Badge variant={variantMap[orderType] || 'outline'} className="text-xs">
              {t(`table.orderTypes.${orderType}`)}
            </Badge>
          );
        },
      },
    ];

    // 🔵 COLUMNA: Acciones (dropdown)
    baseColumns.push({
      headerName: '',
      field: 'actions',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right',
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      // 🎯 CELDA: Dropdown de acciones
      cellRenderer: (params: any) => {
        const client = params.data as Client;
        return (
          <div className="flex justify-end w-full">
            <DropdownActions client={client} onImpersonate={onImpersonate} />
          </div>
        );
      },
    });

    return baseColumns;
  }, [t, onImpersonate]);

  // 🎯 Filtrar columnas basado en la selección del ColumnSelector
  const filteredColumnDefs: ColDef[] = useMemo(() => {
    // Utilidad para evitar columnas duplicadas por `field`
    const dedupeByField = (cols: ColDef[]) => {
      const seen = new Set<string>();
      const result: ColDef[] = [];
      cols.forEach((c) => {
        const id = (c.field || c.headerName || 'unknown') as string;
        if (!seen.has(id)) {
          seen.add(id);
          result.push(c);
        }
      });
      return result;
    };

    if (selectedColumns.length === 0) {
      // Si no hay selección, mostrar todas las columnas (sin duplicados)
      return dedupeByField(allColumnDefs);
    }

    // Filtrar columnas basado en la selección
    const selectedColumnDefs = allColumnDefs.filter((col) => {
      const fieldId = col.field || col.headerName || 'unknown';
      // 🎯 SIEMPRE incluir la columna de acciones, sin importar el filtro
      if (fieldId === 'actions') return true;
      return selectedColumns.includes(fieldId);
    });

    return selectedColumnDefs;
  }, [allColumnDefs, selectedColumns]);

  // 🎯 Columnas para el ColumnSelector (formato TanStack)
  const tanStackColumns: ColumnDef<Client>[] = useMemo(() => {
    return convertToTanStackColumns(allColumnDefs);
  }, [allColumnDefs]);

  return {
    allColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
